import React, { useContext, useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../context/auth/AuthContext';
import { ChatContext } from '../../context/ChatContext/chatContext';
import { BASE_URL } from '../api/url';
import { usePermissions } from '../../hooks/usePermissions';
import { ReactComponent as Logo } from "../../assects/svg/side bar/triva_logo.svg";
import { ReactComponent as DashBoardIcon } from "../../assects/svg/side bar/dashboard.svg";
import { ReactComponent as DashboardActiveIcon } from "../../assects/svg/side bar/dashboard-active.svg";
import { ReactComponent as ClientIcon } from "../../assects/svg/side bar/clients.svg";
import { ReactComponent as ClientActiveIcon } from "../../assects/svg/side bar/clients-active.svg";
import { ReactComponent as ChatIcon } from "../../assects/svg/side bar/message-notif.svg";
import { ReactComponent as ChatActiveIcon } from "../../assects/svg/side bar/message-notif-active.svg";
import { ReactComponent as ProfileIcon } from "../../assects/svg/side bar/profile.svg";
import { ReactComponent as ProfileActiveIcon } from "../../assects/svg/side bar/profile-active.svg";
import { ReactComponent as SalesIcon } from "../../assects/svg/side bar/sales.svg";
import { ReactComponent as SalesActiveIcon } from "../../assects/svg/side bar/sales-active.svg";
import { ReactComponent as MyClientIcon } from "../../assects/svg/side bar/My_clients.svg";
import { ReactComponent as MyClientActiveIcon } from "../../assects/svg/side bar/My_clients-active.svg";
import { ReactComponent as GlobalTempletIcon } from "../../assects/svg/side bar/Global_templet.svg";
import { ReactComponent as GlobalTempletActiveIcon } from "../../assects/svg/side bar/Global_templet-active.svg";
import { ReactComponent as TeamIcon } from "../../assects/svg/side bar/team.svg";
import { ReactComponent as TeamActiveIcon } from "../../assects/svg/side bar/team-active.svg";
import { ReactComponent as FoodLibIcon } from "../../assects/svg/side bar/food_lib.svg";
import { ReactComponent as AdhocMealIcon } from "../../assects/svg/side bar/adhoc_meal.svg";
import { ReactComponent as AdhocMealActiveIcon } from "../../assects/svg/side bar/adhoc_meal-active.svg";

interface SidebarItemProps {
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
  label: string;
  active: boolean;
  badge?: number;
  onClick: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  activeIcon,
  label,
  active,
  badge,
  onClick,
}) => (
  <div className="relative">
    <button
      onClick={onClick}
      className={`w-full flex items-center px-2 md:px-3 xl:px-4 3xl:px-5 py-2 md:py-2 xl:py-2.5 3xl:py-3 rounded-lg transition-all relative ${
        active
          ? "bg-white text-[#1F1F1F] font-medium"
          : "text-white hover:bg-[#1FB3B3]"
      }`}
      style={{ gap: "12px" }}
    >
      <div className="w-4 h-4 md:w-5 md:h-5 xl:w-5 xl:h-5 3xl:w-6 3xl:h-6 flex-shrink-0">
        {active ? activeIcon : icon}
      </div>
      <span className="hidden md:inline text-xs md:text-sm xl:text-base 3xl:text-[18px] font-inter">
        {label}
      </span>
      {badge !== undefined && badge > 0 && (
        <span className={`absolute right-2 md:right-3 top-1/2 -translate-y-1/2 text-[10px] md:text-xs font-bold rounded-full min-w-[16px] md:min-w-[20px] h-4 md:h-5 px-1 md:px-1.5 flex items-center justify-center ${
          active ? "bg-[#00A9A9] text-white" : "bg-white text-[#00A9A9]"
        }`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  </div>
);

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoadingProfile } = useAuth();
  const { unreadCounts } = useContext(ChatContext);
  const { can } = usePermissions();

  // Sales section is shown when the user can see any item that lives inside it.
  const canSeeSales = can('sales', 'view');
  const canSeeSalesClients = can('client', 'view_all');
  const showSalesSection = canSeeSales || canSeeSalesClients;

  // Team section groups Team management and Global Templates together.
  const canSeeTeam = can('admin', 'manage_nutritionists');
  const canSeeTemplates = can('global_template', 'view');
  const showTeamSection = canSeeTeam || canSeeTemplates;

  // Calculate number of users with unread messages (not total message count)
  const unreadUsersCount = useMemo(() => {
    return Object.values(unreadCounts).filter(count => count > 0).length;
  }, [unreadCounts]);

  // Determine active page from URL (location.state.fromGlobal overrides the path check)
  const getActivePage = () => {
    const path = location.pathname;
    if (path.startsWith('/sales-clients')) return 'sales-clients';
    if (path.startsWith('/global-template')) return 'global-template';
    if ((location.state as any)?.fromGlobal) return 'global-template';
    if (path.startsWith('/client')) return 'client';
    if (path.startsWith('/chat')) return 'chat';
    if (path.startsWith('/team')) return 'team';
    if (path.startsWith('/profile')) return 'profile';
    if (path.startsWith('/sales')) return 'sales';
    if (path.startsWith('/food-library/dish')) return 'meal-dish';
    if (path.startsWith('/food-library/ingredient')) return 'meal-ingredient';
    if (path.startsWith('/food-library')) return 'meal';
    if (path.startsWith('/adhoc-meal')) return 'adhoc-meal';
    return 'dashboard';
  };

  const activePage = getActivePage();
  const isMealSectionActive =
    activePage === 'meal' || activePage === 'meal-dish' || activePage === 'meal-ingredient';

  // Keep the Meal section expanded when navigating within it. Users can
  // still toggle it manually via the header row.
  const [isMealOpen, setIsMealOpen] = useState<boolean>(isMealSectionActive);
  useEffect(() => {
    if (isMealSectionActive) setIsMealOpen(true);
  }, [isMealSectionActive]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Get user's full name
  const getUserName = () => {
    if (!user) return 'Loading...';
    const firstName = user.nutrist_fname || '';
    const lastName = user.nutrist_lname || '';
    return `${firstName} ${lastName}`.trim() || 'User';
  };

  // Get user's initials
  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.nutrist_fname || '';
    const lastName = user.nutrist_lname || '';
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    return firstInitial && lastInitial ? `${firstInitial}${lastInitial}` : firstInitial || lastInitial || 'U';
  };

  // Get user's role
  const getUserRole = () => {
    if (!user || !user.nutrist_assign_role) return 'Team member';
    return user.nutrist_assign_role;
  };

  // Get profile image URL
  const getProfileImage = () => {
    if (user?.nutrist_profile) {
      return `${BASE_URL}${user.nutrist_profile}`;
    }
    return null;
  };

  return (
    <div className="flex-shrink-0 h-screen bg-[#00A9A9] flex flex-col w-[60px] md:w-[168px] xl:w-[200px] 2xl:w-[220px] 3xl:w-[240px] transition-all duration-300 overflow-hidden">
      {/* Logo */}
      <div className="flex-shrink-0 px-2 md:px-4 xl:px-5 2xl:px-6 pt-4 md:pt-6 xl:pt-7 2xl:pt-8">
        <Logo className="h-10 w-auto" />
      </div>

      {/* Navigation (scrolls when it overflows; profile below stays pinned) */}
      <nav className="flex-1 min-h-0 sidebar-nav-scroll px-1 md:px-1.5  2xl:px-3 mt-6 md:mt-8 xl:mt-10 2xl:mt-12 pb-2">
        <div className="flex flex-col gap-1 md:gap-1 xl:gap-1.5 2xl:gap-2">
          {/* Section: Main */}
          <SidebarItem
            icon={<DashBoardIcon className="w-full h-full" />}
            activeIcon={<DashboardActiveIcon className="w-full h-full" />}
            label="Dashboard"
            active={activePage === 'dashboard'}
            onClick={() => handleNavigation('/dashboard')}
          />

          <SidebarItem
            icon={<MyClientIcon className="w-full h-full" />}
            activeIcon={<MyClientActiveIcon className="w-full h-full" />}
            label="My Client"
            active={activePage === 'client'}
            onClick={() => handleNavigation('/client')}
          />

          {can('chat', 'access_chat') && (
            <SidebarItem
              icon={<ChatIcon className="w-full h-full" />}
              activeIcon={<ChatActiveIcon className="w-full h-full" />}
              label="Chat"
              active={activePage === 'chat'}
              badge={unreadUsersCount}
              onClick={() => handleNavigation('/chat')}
            />
          )}

          {/* Section: Team & Templates */}
          {showTeamSection && (
            <>
              <div className=" mx-1 border-t border-teal-400 opacity-80" />
              {canSeeTeam && (
                <SidebarItem
                  icon={<TeamIcon className="w-full h-full" />}
                  activeIcon={<TeamActiveIcon className="w-full h-full" />}
                  label="Team"
                  active={activePage === 'team'}
                  onClick={() => handleNavigation('/team')}
                />
              )}
              {canSeeTemplates && (
                <SidebarItem
                  icon={<GlobalTempletIcon className="w-full h-full" />}
                  activeIcon={<GlobalTempletActiveIcon className="w-full h-full" />}
                  label="Templates"
                  active={activePage === 'global-template'}
                  onClick={() => handleNavigation('/global-template')}
                />
              )}
            </>
          )}

          {/* Section: Sales (shown when any sales-area item is permitted) */}
          {showSalesSection && (
            <>
              <div className=" mx-1 border-t border-teal-400 opacity-80" />
              {canSeeSales && (
                <SidebarItem
                  icon={<SalesIcon className="w-full h-full" />}
                  activeIcon={<SalesActiveIcon className="w-full h-full" />}
                  label="Sales"
                  active={activePage === 'sales'}
                  onClick={() => handleNavigation('/sales')}
                />
              )}
              {canSeeSalesClients && (
                <SidebarItem
                  icon={<ClientIcon className="w-full h-full" />}
                  activeIcon={<ClientActiveIcon className="w-full h-full" />}
                  label="Clients"
                  active={activePage === 'sales-clients'}
                  onClick={() => handleNavigation('/sales-clients')}
                />
              )}
            </>
          )}

          {/* Section: Meal (parent header + Dish / Ingredient sub-items) */}
          <div className=" mx-1 border-t border-teal-400 opacity-80" />
          <div className="relative">
            <button
              onClick={() => setIsMealOpen((v) => !v)}
              className={`w-full flex items-center px-2 md:px-3 xl:px-4 3xl:px-5 py-2 md:py-2 xl:py-2.5 3xl:py-3 rounded-lg transition-all ${
                isMealSectionActive
                  ? 'text-white'
                  : 'text-white hover:bg-[#1FB3B3]'
              }`}
              style={{ gap: '12px' }}
            >
              <div className="w-4 h-4 md:w-5 md:h-5 xl:w-5 xl:h-5 3xl:w-6 3xl:h-6 flex-shrink-0">
                <FoodLibIcon className="w-full h-full" />
              </div>
              <span className="hidden md:inline text-xs md:text-sm xl:text-base 3xl:text-[18px] font-inter flex-1 text-left">
                Food Library
              </span>
              <span className="hidden md:inline-flex items-center">
                {isMealOpen ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </span>
            </button>

            {isMealOpen && (
              <div className="hidden md:flex flex-col pl-6 xl:pl-7 3xl:pl-8 mt-1 gap-1">
                {/* Sub-item row: a short vertical accent line renders on
                    the left of the ACTIVE row only (not spanning both). */}
                <div className="relative flex items-center">
                  {activePage === 'meal-dish' && (
                    <span className="absolute left-[-8px] xl:left-[-10px] 3xl:left-[-12px] top-1 bottom-1 w-[2px] bg-white rounded-full" />
                  )}
                  <button
                    onClick={() => handleNavigation('/food-library/dish')}
                    className={`w-full flex items-center px-3 py-1.5 xl:py-2 rounded-md text-xs md:text-sm 3xl:text-[16px] font-inter transition-all text-left ${
                      activePage === 'meal-dish'
                        ? 'bg-white text-[#1F1F1F] font-medium shadow-sm'
                        : 'text-white hover:bg-[#1FB3B3]'
                    }`}
                  >
                    Dish
                  </button>
                </div>

                <div className="relative flex items-center">
                  {activePage === 'meal-ingredient' && (
                    <span className="absolute left-[-8px] xl:left-[-10px] 3xl:left-[-12px] top-1 bottom-1 w-[2px] bg-white rounded-full" />
                  )}
                  <button
                    onClick={() => handleNavigation('/food-library/ingredient')}
                    className={`w-full flex items-center px-3 py-1.5 xl:py-2 rounded-md text-xs md:text-sm 3xl:text-[16px] font-inter transition-all text-left ${
                      activePage === 'meal-ingredient'
                        ? 'bg-white text-[#1F1F1F] font-medium shadow-sm'
                        : 'text-white hover:bg-[#1FB3B3]'
                    }`}
                  >
                    Ingredient
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Adhoc meal plans */}
          <SidebarItem
            icon={<AdhocMealIcon className="w-full h-full" />}
            activeIcon={<AdhocMealActiveIcon className="w-full h-full" />}
            label="Adhoc meal"
            active={activePage === 'adhoc-meal'}
            onClick={() => handleNavigation('/adhoc-meal')}
          />

          {/* Separator before Profile */}
          <div className=" mx-1 border-t border-teal-400 opacity-80" />

          {/* Section: Account */}
          <SidebarItem
            icon={<ProfileIcon className="w-full h-full" />}
            activeIcon={<ProfileActiveIcon className="w-full h-full" />}
            label="Profile"
            active={activePage === 'profile'}
            onClick={() => handleNavigation('/profile')}
          />
        </div>
      </nav>

      {/* User Profile (pinned to bottom) */}
      <div className="flex-shrink-0 px-2 md:px-4 xl:px-5 3xl:px-6 py-3 md:py-4 xl:py-4 2xl:py-5 border-t border-teal-400 bg-[#00A9A9]">
        <div className="flex items-center gap-2 md:gap-3">
          {/* Profile Image or Initials */}
          {getProfileImage() ? (
            <img
              src={getProfileImage()!}
              alt="Profile"
              className="w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10 3xl:w-12 3xl:h-12 rounded-full object-cover flex-shrink-0 border-2 border-white"
            />
          ) : (
            <div className="w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10 3xl:w-12 3xl:h-12 bg-[#DAF9F9] rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white">
              <span className="text-xs md:text-sm xl:text-base 3xl:text-lg font-semibold text-[#00A9A9]">
                {getUserInitials()}
              </span>
            </div>
          )}
          
          {/* User Info */}
          <div className="hidden md:block flex-1 min-w-0">
            {isLoadingProfile ? (
              <div className="space-y-1">
                <div className="h-3 bg-teal-300 rounded animate-pulse w-20"></div>
                <div className="h-2 bg-teal-300 rounded animate-pulse w-16"></div>
              </div>
            ) : (
              <>
                <div className="font-semibold text-xs md:text-sm xl:text-sm 2xl:text-base text-white truncate">
                  {getUserName()}
                </div>
                <div className="text-[10px] md:text-xs xl:text-xs 2xl:text-sm text-teal-100 truncate">
                  {getUserRole()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;